import { ArrowSmRightIcon } from '@heroicons/react/solid'
import { useNavigate } from 'react-router-dom'
import {
  Form,
  Paragraph,
  StickyFooter,
  Title,
  ErrorMissingData,
} from 'src/components'
import EmailHelpBox from 'src/components/Layout/EmailHelpBox'
import {
  useAddOrganization,
  useCreateOrganization,
  useCreateSetupCognitoUser,
  useRegisterNewSubscriber,
} from './queries'
import {
  savedOrganization,
  savedEmail,
  savedPhone,
  savedFirstname,
  savedLastname,
  savedRegToken,
  savedUserPoolRegion,
  savedUserPoolId,
  savedAppClientId,
  savedCountryCode,
  savedUsersAmount,
  savedIndustry,
  savedAllowFeedback,
} from './storage'
import { RegionSelect } from 'src/regions'
import { savedBackendRegion } from 'src/storage'

import InputField from 'src/components/Shared/UIComponents/InputField'
import { Combobox } from 'src/components/Shared/UIComponents/Combobox'
import CustomButton from 'src/components/Shared/UIComponents/Button'
import SelectWithLabel from 'src/components/Shared/UIComponents/Select'

type Props = {
  regToken: string
  userPoolRegion: string
  userPoolId: string
  appClientId: string
}

function Details(props: Props) {
  const navigate = useNavigate()
  const registerNewSubscriber = useRegisterNewSubscriber()
  const createSetupCognitoUser = useCreateSetupCognitoUser()
  const createOrganization = useCreateOrganization()
  const addOrganization = useAddOrganization()

  const [organization, setOrganization] = savedOrganization.useState('')
  const [email, setEmail] = savedEmail.useState('')
  const [phone, setPhone] = savedPhone.useState('')
  const [firstname, setFirstname] = savedFirstname.useState('')
  const [lastname, setLastname] = savedLastname.useState('')
  const [countryCode, setCountryCode] = savedCountryCode.useState('')
  const [userAmount, setUserAmount] = savedUsersAmount.useState('')
  const [industry, setIndustry] = savedIndustry.useState('')
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [allowFeedback, setAllowFeedback] = savedAllowFeedback.useState(true)
  const [backendRegion, setBackendRegion] =
    savedBackendRegion.useState('eu-central-1')

  return (
    <Form
      onSubmit={async (e) => {
        e.preventDefault()

        // needed first to check that organization doesn't exist yet
        await createOrganization.mutateAsync({
          organization,
          region: backendRegion,
          regToken: props.regToken,
        })
        await addOrganization.mutateAsync({
          organization,
          setupuserPoolRegion: props.userPoolRegion,
          setupuserPoolId: props.userPoolId,
          setupuserPoolAppClientId: props.appClientId,
          setupStep: 'setup',
          application: {
            country_code: countryCode,
          },
          backendRegion,
        })

        await Promise.all([
          registerNewSubscriber.mutateAsync({
            regToken: props.regToken,
            organization,
            username: organization,
            email,
            phone,
            userAmount,
            industry,
            allowFeedback,
            backendRegion,
          }),
          createSetupCognitoUser.mutateAsync({
            organization,
            username: organization,
            email,
            phone,
            firstname,
            lastname,
            region: backendRegion,
          }),
        ])
        navigate('../login')
      }}
    >
      <Title label="Details" className="col-span-full" />
      <Paragraph className="col-span-full">
        You'll need to fill in some basic information
      </Paragraph>
      <InputField
        label="Organization name"
        placeholder="idealsystems"
        value={organization}
        setValue={(value) => setOrganization(value.toLowerCase().trim())}
        title="an organization can only contain lowercase alphanumeric characters and dashes (no spaces allowed)"
        pattern="[a-z0-9](?:[a-z0-9\-]{0,61}[a-z0-9])?"
        tooltip={
          <div className="flex flex-col gap-5">
            <p>
              You can only create one organization per AWS account. Please enter
              the actual name of your organization. You won't be able to change
              this organization name afterwards.{' '}
            </p>
            <p>
              This organisation name is used by users to login into the app.
            </p>
          </div>
        }
      />
      <RegionSelect
        title="Region (Amazon Connect instance)"
        value={backendRegion}
        onChange={setBackendRegion}
        tooltip={
          <div className="flex flex-col gap-5">
            <p>
              Please select the region in which your Amazon Connect instance is
              located. Mobile Connect will be unable to operate if you select an
              AWS region in which you don't have Amazon Connect set up.
            </p>
            <p>
              If you don't know to which region your Amazon Connect instance is
              linked go to<strong> account &gt; Bills </strong>in your
              <strong> AWS console</strong> and search for
              <strong> “Amazon Connect”</strong> under
              <strong> active services.</strong> When unfolding the service, you
              will see the region to which it is linked.
            </p>
          </div>
        }
      />
      <InputField
        label="First name"
        placeholder="John"
        value={firstname}
        setValue={setFirstname}
      />
      <InputField
        label="Last name"
        placeholder="Doe"
        value={lastname}
        setValue={setLastname}
      />
      <InputField
        label="Email"
        placeholder="john.doe@gmail.com"
        value={email}
        setValue={(value) => setEmail(value.trim())}
        type="email"
      />
      <InputField
        label="Phone number"
        placeholder="+32 2 300 56 96"
        value={phone}
        setValue={setPhone}
        type="tel"
        required={false}
      />

      <Combobox
        label="Default Country Code (dial-out)"
        placeholder="Belgium"
        setValue={setCountryCode}
        value={countryCode}
        tooltip={
          <p>
            This will be the default dial-out country code that users will see
            in the Mobile App. We advise to select the country code to which the
            majority of your outbound calls are directed. Users have the option
            in the Mobile App to select a different country code at any time
            when dialling out.
          </p>
        }
      />

      <SelectWithLabel
        title="How many users will use Mobile Connect?"
        options={userAmountOptions}
        value={userAmount || undefined}
        onChange={setUserAmount}
      />
      <SelectWithLabel
        title="What industry is your organization in?"
        options={industryOptions}
        value={industry || undefined}
        onChange={setIndustry}
      />
      <StickyFooter>
        <EmailHelpBox className="flex-1" />
        <CustomButton
          type="submit"
          label="next"
          icon={<ArrowSmRightIcon />}
          iconPosition="right"
          isLoading={
            registerNewSubscriber.isLoading ||
            createSetupCognitoUser.isLoading ||
            addOrganization.isLoading
          }
          disabled={!userAmount || !industry || countryCode.trim().length === 0}
        />
      </StickyFooter>
    </Form>
  )
}

const makeSelectOptions = (options: string[]) =>
  options.map((item) => ({ key: item, name: item }))

const userAmountOptions = makeSelectOptions([
  '1-10',
  '11-100',
  '101-500',
  '500+',
])

const industryOptions = makeSelectOptions([
  'Automobiles and Components',
  'Banks',
  'Capital Goods',
  'Commercial and Professional Services',
  'Consumer Durables and Apparel',
  'Consumer Services',
  'Diversified Financials',
  'Energy',
  'Food, Beverage, and Tobacco',
  'Food and Staples Retailing',
  'Health Care Equipment and Services',
  'Household and Personal Products',
  'Insurance',
  'Materials',
  'Media and Entertainment',
  'Pharmaceuticals, Biotechnology, and Life Sciences',
  'Real Estate',
  'Retailing',
  'Semiconductors and Semiconductor Equipment',
  'Software and Services',
  'Technology Hardware and Equipment',
  'Telecommunication Services',
  'Transportation',
  'Utilities',
  'Other',
])

export default function ValidateDetails() {
  const [regToken] = savedRegToken.useState('')
  const [userPoolRegion] = savedUserPoolRegion.useState('')
  const [userPoolId] = savedUserPoolId.useState('')
  const [appClientId] = savedAppClientId.useState('')

  if (!regToken || !userPoolRegion || !userPoolId || !appClientId) {
    return (
      <ErrorMissingData
        missingValues={{
          regtoken: !regToken,
          setupuserPoolRegion: !userPoolRegion,
          setupuserPoolId: !userPoolId,
          setupuserPoolAppClientId: !appClientId,
        }}
      />
    )
  }

  return (
    <Details
      regToken={regToken}
      userPoolRegion={userPoolRegion}
      userPoolId={userPoolId}
      appClientId={appClientId}
    />
  )
}
